import { useState, useCallback, useMemo } from 'react';
function isDateRange(v) {
    return (typeof v === 'object' &&
        v !== null &&
        !Array.isArray(v) &&
        !(v instanceof Date) &&
        'from' in v &&
        'to' in v);
}
function defaultValueForKind(kind) {
    switch (kind) {
        case 'text':
        case 'select-single':
        case 'autocomplete-single':
        case 'date':
        case 'date-range':
            return null;
        case 'select-multi':
        case 'autocomplete-multi':
            return [];
        case 'toggle':
            return false;
        default:
            return null;
    }
}
/**
 * Build the `{ [id]: initialValue }` map from a FilterConfig array, applying
 * per-filter `initialValue` overrides and falling back to kind defaults.
 */
export function initialValuesFromConfigs(configs) {
    return configs.reduce((acc, c) => {
        acc[c.id] =
            c.initialValue !== undefined
                ? c.initialValue
                : defaultValueForKind(c.kind);
        return acc;
    }, {});
}
/**
 * Manages filter display + typed values + per-filter label cache for a
 * filtered table page.
 *
 * - `displayed` — which filters are visible in the bar
 * - `values` — current filter values (fully typed via generic T)
 * - `active` — derived: displayed filters that have a non-empty value
 * - `labels` — id→label map per filter key, written by autocomplete editors
 *   when the user selects an option, read by chips so they can render
 *   display strings without the page storing sidecar entities
 * - `setValue` — typed setter (autocomplete on keys, type-safe values)
 * - `setLabel` — record a label for a given option id under a filter key
 * - `clearLabels` — drop all labels for a filter key (used on reset/hide)
 * - `toggle` — show/hide a filter; hiding clears value + labels
 * - `reset` — restore all values + labels + display all filters
 *
 * Usage:
 * ```ts
 * const filters = useFilterState<AccFilters>({
 *   carrier_statuses: ['pending-admin'],
 *   carrier: [],
 * });
 *
 * filters.setValue('carrier', [...filters.values.carrier, 'abc-123']);
 * filters.setLabel('carrier', 'abc-123', 'Acme Logistics');
 * filters.labels.carrier['abc-123']  // 'Acme Logistics'
 * ```
 */
export function useFilterState(initialValues) {
    const allKeys = useMemo(() => Object.keys(initialValues), [initialValues]);
    const [displayed, setDisplayed] = useState(allKeys);
    const [values, setValues] = useState(initialValues);
    const [labels, setLabels] = useState(() => allKeys.reduce((acc, k) => {
        acc[k] = {};
        return acc;
    }, {}));
    const setValue = useCallback((key, value) => {
        setValues((prev) => (Object.assign(Object.assign({}, prev), { [key]: value })));
    }, []);
    const setMany = useCallback((updates) => {
        setValues((prev) => (Object.assign(Object.assign({}, prev), updates)));
    }, []);
    const setLabel = useCallback((key, id, label) => {
        setLabels((prev) => (Object.assign(Object.assign({}, prev), { [key]: Object.assign(Object.assign({}, (prev[key] || {})), { [id]: label }) })));
    }, []);
    const clearLabels = useCallback((key) => {
        setLabels((prev) => (Object.assign(Object.assign({}, prev), { [key]: {} })));
    }, []);
    const toggle = useCallback((key) => {
        setDisplayed((prev) => {
            if (prev.includes(key)) {
                // Hide + clear value and labels (lane rates UX)
                setValues((prevValues) => (Object.assign(Object.assign({}, prevValues), { [key]: initialValues[key] })));
                setLabels((prevLabels) => (Object.assign(Object.assign({}, prevLabels), { [key]: {} })));
                return prev.filter((k) => k !== key);
            }
            return [...prev, key];
        });
    }, [initialValues]);
    const reset = useCallback(() => {
        setValues(initialValues);
        setDisplayed(allKeys);
        setLabels(allKeys.reduce((acc, k) => {
            acc[k] = {};
            return acc;
        }, {}));
    }, [initialValues, allKeys]);
    const active = useMemo(() => {
        return displayed.filter((id) => {
            const v = values[id];
            if (v == null)
                return false;
            if (v === '')
                return false;
            if (v === false)
                return false;
            if (Array.isArray(v) && v.length === 0)
                return false;
            if (isDateRange(v)) {
                return v.from != null && v.from !== '' && v.to != null && v.to !== '';
            }
            return true;
        });
    }, [displayed, values]);
    return {
        displayed,
        values,
        active,
        labels,
        setValue,
        setMany,
        setLabel,
        clearLabels,
        toggle,
        reset,
    };
}
